import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, ArrowUpRight } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";

const navItems = [
  { label: "Services", href: "#services" },
  { label: "Ethos", href: "#ethos" },
  { label: "Apps", href: "#apps" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkHref = (hash) => (onHome ? hash : `/${hash}`);

  return (
    <motion.header
      initial={{ y: -90 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-6 lg:px-12 h-[70px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-primary text-primary-foreground font-display font-semibold">
            J
          </span>
          <span className="font-display text-[15px] font-semibold tracking-[0.18em] uppercase">
            JLY&nbsp;Services
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {navItems.map((item, i) => (
            <a
              key={item.label}
              href={linkHref(item.href)}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className="group relative text-[13px] font-medium tracking-wide text-foreground/70 hover:text-foreground transition-colors duration-200"
            >
              <span className="text-accent-warm mr-1.5 font-display text-[11px]">0{i + 1}</span>
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-foreground transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label="Toggle theme"
            className="grid place-items-center h-10 w-10 rounded-full border border-border hover:bg-muted transition-colors duration-200"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>
          <a
            href={linkHref("#contact")}
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-foreground text-background pl-5 pr-4 py-2.5 text-[13px] font-medium hover:bg-primary hover:text-primary-foreground transition-colors duration-300 group"
          >
            Get a quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            className="md:hidden grid place-items-center h-10 w-10 rounded-full border border-border"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={linkHref(item.href)}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                  className="font-display text-2xl font-medium text-foreground/85 hover:text-foreground"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
